import { looksLikePlaceId } from "./myPlaces";
import { MetaPlace } from "./MetaPlace";
import { isMeta } from "./metaPlace";

const META = "__meta";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { LoroDoc } from "loro-crdt";
import { setProp } from "../core/ops";
import type { PropVal, Task } from "../core/schema";
import type { SignalAst } from "../core/signals";
import { Button, Checkbox, Input, Select } from "../ui";
import { describeCondition, signalLabel, type SignalDef } from "./describe";

type Op = "eq" | "neq" | "gt" | "lt";

/**
 * The task panel's "When" (I8): one condition on a signal. While it holds the task rises in lists, while it
 * does not it steps back; "hide" keeps it out of lists while the condition is definitely false. Unknown
 * signals never hide anything.
 */
export function WhenEditor({
  task,
  ro,
  write,
  definitions,
  places,
  placeList = [],
  placeLists = [],
  myPlaces = [],
  myLists = [],
  workspaceId,
}: {
  task: Task;
  ro: boolean;
  write(fn: (doc: LoroDoc) => void): void;
  definitions: Record<string, SignalDef>;
  places: string[];
  placeList?: { id: string; name: string }[];
  /** several places under one name (docs/plans/buttons-and-place-lists.md, part 3) */
  placeLists?: { id: string; name: string }[];
  /** in a shared space: my private places (P1), which only I see */
  myPlaces?: { id: string; name: string }[];
  /** in a shared space: my private place lists */
  myLists?: { id: string; name: string }[];
  workspaceId: string;
}) {
  const { t } = useTranslation();
  const current = (task.props.when ?? null) as SignalAst | null;
  const hide = task.props.whenHide === true;
  const names = Object.keys(definitions).sort();
  const first = current && typeof current.signal === "string" ? current : null;
  const firstOp = first ? ((["eq", "neq", "gt", "lt"] as Op[]).find((o) => o in first) ?? "eq") : "eq";
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState<string>(first ? String(first.signal) : (names[0] ?? ""));
  const [op, setOp] = useState<Op>(firstOp);
  const [value, setValue] = useState<string>(first ? String(first[firstOp] ?? "") : "");
  const [hideIt, setHideIt] = useState(hide);
  const def = definitions[name];
  const type = def?.type ?? "text";

  const save = () => {
    const v: unknown = type === "boolean" ? value === "true" : type === "number" ? Number(value) : value;
    const ast = { signal: name, [op]: v };
    write((doc) => {
      setProp(doc, task.id, "when", ast as unknown as PropVal);
      setProp(doc, task.id, "whenHide", hideIt ? true : null);
    });
    setEditing(false);
  };
  const remove = () => {
    write((doc) => {
      setProp(doc, task.id, "when", null);
      setProp(doc, task.id, "whenHide", null);
    });
    setEditing(false);
  };

  const atPlace = typeof task.props.atPlace === "string" ? task.props.atPlace : "";
  // a kind of place (ADR 0025): "meta:pharmacy" reminds near any pharmacy in my search areas
  const [metaOpen, setMetaOpen] = useState(false);
  const meta = isMeta(atPlace) || metaOpen;
  const placeKnown = [...placeList, ...myPlaces, ...placeLists, ...myLists].some((p) => p.id === atPlace);
  const lists = [...placeLists, ...myLists];
  const placeRow = (
    <div className="flex flex-col gap-1.5">
      <label className="flex flex-wrap items-center gap-2 text-[13px]" data-testid="at-place">
        <span>{t("signals.atPlace")}</span>
        <Select
          compact
          disabled={ro}
          aria-label={t("signals.atPlace")}
          value={meta ? META : atPlace}
          onChange={(e) => {
            if (e.target.value === META) {
              setMetaOpen(true);
              return;
            }
            setMetaOpen(false);
            write((doc) => setProp(doc, task.id, "atPlace", e.target.value ? e.target.value : null));
          }}
          data-testid="at-place-select"
        >
          <option value="">{t("signals.atPlaceNone")}</option>
          <option value={META}>{t("metaPlaces.kindOfPlace")}</option>
          {myPlaces.length > 0 ? (
            <>
              <optgroup label={t("signals.places.shared")}>
                {placeList.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label={t("signals.places.mine")}>
                {myPlaces.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </optgroup>
            </>
          ) : (
            placeList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))
          )}
          {lists.length > 0 && (
            <optgroup label={t("signals.lists.title")} data-testid="at-place-lists">
              {lists.map((p) => (
                <option key={p.id} value={p.id}>
                  {t("signals.lists.option", { name: p.name })}
                </option>
              ))}
            </optgroup>
          )}
          {atPlace && !placeKnown && !isMeta(atPlace) && (
            <option value={atPlace}>{looksLikePlaceId(atPlace) ? t("signals.privatePlace") : atPlace}</option>
          )}
        </Select>
      </label>
      {meta && <MetaPlace task={task} workspaceId={workspaceId} ro={ro} write={write} />}
    </div>
  );

  if (names.length === 0 && !current)
    return (
      <div className="flex flex-col gap-2">
        {placeRow}
        <p className="text-[12.5px] text-neutral-500">{t("signals.noneYet")}</p>
      </div>
    );

  if (!editing)
    return (
      <div className="flex flex-col gap-1.5" data-testid="when">
        {placeRow}
        {current ? (
          <p className="text-[13px]" data-testid="when-text">
            {describeCondition(current, definitions, t)}
            {hide && <span className="text-neutral-500"> · {t("signals.hiddenWhenNot")}</span>}
          </p>
        ) : (
          <p className="text-[12.5px] text-neutral-500">{t("signals.whenHint")}</p>
        )}
        {!ro && (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="when-edit">
              {current ? t("signals.change") : t("signals.add")}
            </Button>
            {current && (
              <Button size="sm" variant="ghost" onClick={remove}>
                {t("signals.remove")}
              </Button>
            )}
          </div>
        )}
      </div>
    );

  return (
    <div className="flex flex-col gap-2" data-testid="when-form">
      <div className="flex flex-wrap gap-2">
        <Select
          compact
          aria-label={t("signals.signal")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="when-signal"
        >
          {names.map((n) => (
            <option key={n} value={n}>
              {signalLabel(n, definitions[n], t)}
            </option>
          ))}
        </Select>
        <Select
          compact
          aria-label={t("signals.operator")}
          value={op}
          onChange={(e) => setOp(e.target.value as Op)}
          data-testid="when-op"
        >
          <option value="eq">{t("signals.opShort.eq")}</option>
          <option value="neq">{t("signals.opShort.neq")}</option>
          {type === "number" && <option value="gt">{t("signals.opShort.gt")}</option>}
          {type === "number" && <option value="lt">{t("signals.opShort.lt")}</option>}
        </Select>
        {type === "boolean" ? (
          <Select
            compact
            aria-label={t("signals.value")}
            value={value || "true"}
            onChange={(e) => setValue(e.target.value)}
            data-testid="when-value"
          >
            <option value="true">{t("signals.yes")}</option>
            <option value="false">{t("signals.no")}</option>
          </Select>
        ) : (
          <>
            <Input
              compact
              aria-label={t("signals.value")}
              type={type === "number" ? "number" : "text"}
              list={name === "place.current" ? "when-places" : undefined}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-36"
              data-testid="when-value"
            />
            {name === "place.current" && (
              <datalist id="when-places">
                {places.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            )}
          </>
        )}
      </div>
      <label className="flex items-center gap-2 text-[13px]">
        <Checkbox checked={hideIt} label={t("signals.hideWhenNot")} onChange={setHideIt} />
        <span>{t("signals.hideWhenNot")}</span>
      </label>
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={save}
          disabled={!name || (type !== "boolean" && value === "")}
          data-testid="when-save"
        >
          {t("signals.save")}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
          {t("signals.cancel")}
        </Button>
      </div>
    </div>
  );
}
